import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SEO } from "@/components/SEO";
import {
  getOrganizationSchema,
  getWebSiteSchema,
  getSoftwareApplicationSchema,
  getFAQPageSchema,
  getHowToSchema,
  homepageFAQs,
  homepageHowToSteps,
} from "@/lib/schema";
import {
  Check,
  ArrowRight,
  X,
  ExternalLink,
  Lightbulb,
  PlusCircle,
  MinusCircle,
  CreditCard,
  BarChart3,
  Users,
  Maximize2,
  Palette,
  Rocket,
  TrendingUp,
  ShoppingCart,
  UserPlus,
  Mail,
  Tag,
  Smartphone,
  RefreshCw,
  BarChart,
  ShoppingBag,
  Target,
  Zap,
  Eye,
  DollarSign,
  Search,
  Smile,
  Star,
  AlertTriangle
} from "lucide-react";
import { Hero } from "@/components/ui/animated-hero";
import { FlickeringGrid } from "@/components/ui/flickering-grid";
import { Stats } from "@/components/ui/stats";
import { useState, lazy, Suspense, useRef } from "react";
import { Tiles } from "@/components/ui/tiles";
import { SectionLoader } from "@/components/ui/section-loader";
import { LazyVideo } from "@/components/ui/lazy-video";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useAutumnSubscription } from "@/hooks/useAutumnSubscription";
import { useAuth } from "@/components/AuthProvider";

const steps = [
  {
    number: "1",
    icon: Palette,
    title: "Choose What You're Testing",
    description: "Pick your validation type — payment intent, waitlist signups, or pricing sensitivity.",
    gradient: "from-indigo-500/20 to-blue-500/20",
    items: [
      "Payment, waitlist, or pricing",
      "Pre-loaded templates",
      "No blank page paralysis"
    ]
  },
  {
    number: "2",
    icon: Rocket,
    title: "Build Your Flow",
    description: "A guided builder walks you through product details, flow type, design, and your thank-you page.",
    gradient: "from-violet-500/20 to-indigo-500/20",
    items: [
      "Step-by-step guided builder",
      "Customize branding and copy",
      "No design skills needed"
    ]
  },
  {
    icon: TrendingUp,
    title: "Launch, Share & Read the Signal",
    description: "Share a direct link or embed it anywhere. See who clicked, who converted, and where people dropped off.",
    gradient: "from-blue-500/20 to-sky-500/20",
    items: [
      "Direct link or embed publishing",
      "Real-time conversion tracking",
      "Drop-off analysis by step"
    ]
  }
];

const conversionSteps = [
  {
    icon: ShoppingCart,
    title: "Payment Simulation",
    description: "Your visitors see a realistic checkout flow without actual payment processing"
  },
  {
    icon: BarChart3,
    title: "Conversion Tracking",
    description: "Every visit and conversion attempt is tracked and analyzed"
  },
  {
    icon: Smartphone,
    title: "Responsive Design",
    description: "Flows work seamlessly on mobile, tablet, and desktop devices"
  },
  {
    icon: Mail,
    title: "Email Collection",
    description: "Capture interested customers' emails for future launches"
  }
];

// Lazy load sections
const HowItWorksSection = lazy(() => import("@/components/sections/HowItWorks"));
const WhyItWorksSection = lazy(() => import("@/components/sections/WhyItWorks"));
const FAQSection = lazy(() => import("@/components/sections/FAQ"));
const TestimonialsSection = lazy(() => import("@/components/sections/Testimonials"));

// Pricing tiers
const pricingTiers = [
  {
    name: "Monthly",
    price: "19",
    period: "/month",
    description: "For founders who test ideas seriously.",
    features: [
      { icon: Rocket, text: "Up to 12 active validation flows" },
      { icon: CreditCard, text: "All 8 flow types (payment, waitlist, pricing & more)" },
      { icon: BarChart3, text: "Full analytics with conversion tracking" },
      { icon: Zap, text: "Embed publishing & direct links" },
      { icon: Eye, text: "Custom branding on flows" },
      { icon: Mail, text: "Priority email support" },
      { icon: RefreshCw, text: "Export conversion data (CSV)" },
    ],
    isPopular: false,
    planId: "monthly",
  },
  {
    name: "Yearly",
    price: "9",
    period: "/month",
    billingNote: "Billed yearly at $108",
    description: "Save 53% compared to monthly.",
    features: [
      { icon: Rocket, text: "Up to 12 active validation flows" },
      { icon: CreditCard, text: "All 8 flow types (payment, waitlist, pricing & more)" },
      { icon: BarChart3, text: "Full analytics with conversion tracking" },
      { icon: Zap, text: "Embed publishing & direct links" },
      { icon: Eye, text: "Custom branding on flows" },
      { icon: Mail, text: "Priority email support" },
      { icon: RefreshCw, text: "Export conversion data (CSV)" },
    ],
    isPopular: true,
    planId: "yearly",
  },
];

const flowTypes = [
  {
    id: "payment",
    icon: CreditCard,
    title: "Payment Test",
    description: "Find out if people will pull out their credit card",
    isPopular: true
  },
  {
    id: "waitlist",
    icon: UserPlus,
    title: "Waitlist Builder",
    description: "Separate real interest from empty enthusiasm",
    isPopular: true
  },
  {
    id: "subscription",
    icon: RefreshCw,
    title: "Subscription Validator",
    description: "Test if they'll pay monthly (spoiler: it's harder)",
    isPopular: false
  },
  {
    id: "pricing-table",
    icon: BarChart,
    title: "Pricing Optimizer",
    description: "Test 3 prices. Find the sweet spot.",
    isPopular: false
  },
  {
    id: "pre-order",
    icon: ShoppingBag,
    title: "Pre-order Test",
    description: "Validate demand for products that don't exist yet",
    isPopular: false
  },
  {
    id: "discount",
    icon: Tag,
    title: "Discount Analyzer",
    description: "Learn what price makes them click \"Buy\"",
    isPopular: false
  },
  {
    id: "referral",
    icon: Users,
    title: "Referral Simulator",
    description: "Test if users will actually share",
    isPopular: false
  },
  {
    id: "newsletter",
    icon: Mail,
    title: "Newsletter Magnet",
    description: "See if your content is worth their inbox",
    isPopular: false
  }
];

const Index = () => {
  const { startSubscription, isLoading } = useAutumnSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [openFaqItem, setOpenFaqItem] = useState<number | null>(0);
  const [isHoveringVideo1, setIsHoveringVideo1] = useState(false);
  const [isHoveringVideo2, setIsHoveringVideo2] = useState(false);
  const [isHoveringVideo3, setIsHoveringVideo3] = useState(false);

  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const video3Ref = useRef<HTMLVideoElement>(null);

  const toggleFaqItem = (index: number) => {
    setOpenFaqItem(openFaqItem === index ? null : index);
  };

  const handleFullscreen = async (videoRef: React.RefObject<HTMLVideoElement>) => {
    if (!document.fullscreenElement) {
      try {
        await videoRef.current?.requestFullscreen();
      } catch (err) {
        // Fallback for browsers that don't support requestFullscreen
        try {
          await (videoRef.current as any)?.webkitRequestFullscreen();
        } catch (err) {
          // Fullscreen failed silently
        }
      }
    } else {
      await document.exitFullscreen();
    }
  };

  return (
    <>
      <SEO
        title="WillTheyConvert - Validate Your Product Ideas Before Building"
        description="Test if people will pay for your product idea before building it. Create fake door tests in minutes and validate your startup idea with real users."
        keywords="startup validation, fake door testing, MVP testing, product validation, idea validation, startup testing, pre-launch validation"
        canonicalUrl="/"
        ogTitle="WillTheyConvert - Validate Your Product Ideas Before Building"
        ogDescription="Test if people will pay for your product idea before building it. Create fake door tests and validate with real users."
        jsonLd={[
          getOrganizationSchema(),
          getWebSiteSchema(),
          getSoftwareApplicationSchema(),
          getFAQPageSchema(homepageFAQs),
          getHowToSchema(
            "How to Validate Your Startup Idea",
            "Learn how to validate your product idea before building using fake door testing.",
            homepageHowToSteps
          ),
        ]}
      />

      {/* Background Tiles */}
      <div className="fixed inset-0 w-full h-full pointer-events-none z-0 overflow-hidden">
        <Tiles rows={50} cols={4} className="opacity-30 sm:hidden" />
        <Tiles rows={50} cols={8} className="opacity-30 hidden sm:block" />
      </div>
      
      {/* Main Content */}
      <div className="relative min-h-screen flex flex-col z-10 overflow-x-hidden">
        <div className="relative z-10">
          {/* Hero Section */}
          <Hero />

          {/* Strategy Tested By - Minimal Section */}
          <section className="py-6 bg-gradient-to-r from-indigo-50/30 to-violet-50/30">
            <div className="container max-w-6xl mx-auto px-4">
              <div className="flex flex-col items-center">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-4 font-semibold">Strategy tested by</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 w-full max-w-3xl">
                  <Link to="/case-studies" className="group">
                    <motion.div
                      whileHover={{ y: -2, scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col items-center bg-white/60 p-3 rounded-xl hover:shadow-md transition-all border border-gray-200 hover:border-indigo-300 h-full"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 p-1.5 mb-2 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <img src="/images/dropbox.webp" alt="Dropbox" className="w-full h-full object-contain filter brightness-0 invert" />
                      </div>
                      <h4 className="text-xs font-semibold text-gray-900 mb-1">Dropbox</h4>
                      <div className="text-center min-h-[40px] flex flex-col justify-center">
                        <p className="text-xs text-gray-600 line-clamp-2">75,000 signups before building</p>
                      </div>
                    </motion.div>
                  </Link>

                  <Link to="/case-studies" className="group">
                    <motion.div
                      whileHover={{ y: -2, scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col items-center bg-white/60 p-3 rounded-xl hover:shadow-md transition-all border border-gray-200 hover:border-pink-300 h-full"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 p-1.5 mb-2 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <img src="/images/airbnb.webp" alt="Airbnb" className="w-full h-full object-contain filter brightness-0 invert" />
                      </div>
                      <h4 className="text-xs font-semibold text-gray-900 mb-1">Airbnb</h4>
                      <div className="text-center min-h-[40px] flex flex-col justify-center">
                        <p className="text-xs text-gray-600 line-clamp-2">Validated demand before coding</p>
                      </div>
                    </motion.div>
                  </Link>

                  <Link to="/case-studies" className="group">
                    <motion.div
                      whileHover={{ y: -2, scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col items-center bg-white/60 p-3 rounded-xl hover:shadow-md transition-all border border-gray-200 hover:border-orange-300 h-full"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 mb-2 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <span className="text-xl font-black text-white">P</span>
                      </div>
                      <h4 className="text-xs font-semibold text-gray-900 mb-1">Product Hunt</h4>
                      <div className="text-center min-h-[40px] flex flex-col justify-center">
                        <p className="text-xs text-gray-600 line-clamp-2">MVP for community-driven discovery</p>
                      </div>
                    </motion.div>
                  </Link>

                  <Link to="/case-studies" className="group">
                    <motion.div
                      whileHover={{ y: -2, scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col items-center bg-white/60 p-3 rounded-xl hover:shadow-md transition-all border border-gray-200 hover:border-green-300 h-full"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 p-1.5 mb-2 flex items-center justify-center group-hover:scale-105 transition-transform">
                        <img src="/images/groupon.webp" alt="Groupon" className="w-full h-full object-contain filter brightness-0 invert" />
                      </div>
                      <h4 className="text-xs font-semibold text-gray-900 mb-1">Groupon</h4>
                      <div className="text-center min-h-[40px] flex flex-col justify-center">
                        <p className="text-xs text-gray-600 line-clamp-2">WordPress test before $1B valuation</p>
                      </div>
                    </motion.div>
                  </Link>
                </div>

                <p className="text-sm text-gray-500 mt-4 font-medium">Join founders who stop guessing and start knowing — before writing a single line of code.</p>
              </div>
            </div>
          </section>

          {/* Why Validation Matters - Stat Section (commented out)
          <section className="py-12 bg-gradient-to-b from-white to-gray-50/50">
            <div className="container max-w-3xl mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative rounded-2xl border border-gray-200 bg-white px-8 py-8 flex flex-col sm:flex-row items-center gap-6"
              >
                <div className="absolute -top-4 right-6 bg-white px-2">
                  <AlertTriangle className="w-7 h-7 text-amber-500" />
                </div>
                <p className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-rose-600 shrink-0">
                  42%
                </p>
                <div className="text-center sm:text-left">
                  <p className="text-lg md:text-xl font-semibold text-gray-900 mb-1">
                    of startups fail because nobody wanted what they built.
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    CB Insights found the #1 reason startups fail is building without market demand.
                    WillTheyConvert helps you validate demand before you build — so your time and money go into ideas that actually convert.
                  </p>
                  <p className="text-xs text-gray-600/60 mt-2">Source: CB Insights</p>
                </div>
              </motion.div>
            </div>
          </section>
          */}

          {/* Validation Process Section - Replacing both Problem-Solution and How It Works */}
          <section id="how-it-works" className="py-16 bg-gradient-to-b from-gray-50/50 to-white">
            <div className="container max-w-6xl mx-auto px-4">
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 mb-6"
                >
                  <Target className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium text-indigo-600">The Problem</span>
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900"
                >
                  Most Founders Build the{' '}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">Wrong Thing</span>{' '}
                  First
                </motion.h2>
                <div className="max-w-2xl mx-auto space-y-4">
                  <motion.p
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="text-gray-500 text-lg leading-relaxed"
                  >
                    You have an idea. You spend weeks building it. You launch. And then... <span className="text-gray-900 font-medium">nothing.</span>
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="text-gray-500 text-base leading-relaxed"
                  >
                    The brutal truth: the idea wasn't the problem. <span className="font-semibold text-gray-900">The validation was.</span>{' '}
                    You asked <span className="italic">"would you use this?"</span> instead of <span className="italic font-medium text-indigo-600">"will you pay for this?"</span>
                  </motion.p>
                </div>
              </div>

              {/* Before / After Comparison */}
              <div className="max-w-3xl mx-auto mb-14">
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.97 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-0 rounded-[28px] overflow-hidden shadow-xl shadow-indigo-500/5 border border-gray-200/80"
                >
                  {/* Before */}
                  <div className="bg-gray-50 p-7 md:p-8 relative">
                    <div className="absolute top-0 right-0 md:right-auto md:left-0 w-full md:w-auto h-px md:h-full md:w-px bg-gradient-to-r md:bg-gradient-to-b from-transparent via-gray-200 to-transparent hidden md:block md:left-auto md:right-0" />
                    <motion.div
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                      className="flex items-center gap-3 mb-5"
                    >
                      <div className="w-9 h-9 rounded-xl bg-gray-200/80 flex items-center justify-center">
                        <X className="w-4 h-4 text-gray-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">Before</h3>
                        <p className="text-xs text-gray-400">The old way</p>
                      </div>
                    </motion.div>
                    <ul className="space-y-3.5">
                      {[
                        "Months building a product",
                        "Guessing what people want",
                        "Launching to crickets",
                        "Running out of runway",
                      ].map((item, i) => (
                        <motion.li
                          key={item}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: 0.4 + i * 0.08 }}
                          className="flex items-center gap-3 text-sm text-gray-500"
                        >
                          <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0">
                            <X className="w-3 h-3 text-gray-300" />
                          </div>
                          <span className="line-through decoration-gray-300">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* After */}
                  <div className="bg-white p-7 md:p-8 relative">
                    <motion.div
                      initial={{ opacity: 0, x: 15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                      className="flex items-center gap-3 mb-5"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.5 }}
                        className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md shadow-indigo-500/20"
                      >
                        <Check className="w-4 h-4 text-white" />
                      </motion.div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">After WillTheyConvert</h3>
                        <p className="text-xs text-indigo-500">The smart way</p>
                      </div>
                    </motion.div>
                    <ul className="space-y-3.5">
                      {[
                        "Test demand in 10 minutes",
                        "Real data from real people",
                        "Build what already has buyers",
                        "Move fast with confidence",
                      ].map((item, i) => (
                        <motion.li
                          key={item}
                          initial={{ opacity: 0, x: 15 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.35, delay: 0.5 + i * 0.12 }}
                          className="flex items-center gap-3 text-sm text-gray-700"
                        >
                          <motion.div
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.6 + i * 0.12 }}
                            className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0"
                          >
                            <Check className="w-3 h-3 text-indigo-600" />
                          </motion.div>
                          <span className="font-medium">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  className="text-center text-sm text-gray-400 mt-5"
                >
                  There's a difference between someone liking your idea and someone pulling out their card.
                </motion.p>
              </div>
              

            </div>
          </section>

          {/* Conversion Testing Made Simple */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
                  Conversion Testing Made Simple
                </h2>
                <p className="text-gray-600 text-lg">Stop asking if people want it. Start knowing if they'll pay for it.</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { icon: ShoppingBag, title: "Buy Buttons", description: "See who actually clicks purchase", color: "from-indigo-600 to-violet-600", demoHash: "payment" },
                  { icon: Tag, title: "Pricing Pages", description: "Find what people will really pay", color: "from-purple-500 to-pink-500", demoHash: "pricing-table" },
                  { icon: Mail, title: "Signups", description: "Test if they'll give their email", color: "from-blue-500 to-cyan-500", demoHash: "newsletter" },
                  { icon: Users, title: "Waitlists", description: "Measure real demand, not curiosity", color: "from-violet-500 to-purple-500", demoHash: "waitlist" },
                  { icon: TrendingUp, title: "Upgrades", description: "Validate premium features", color: "from-pink-500 to-rose-500", demoHash: "subscription" },
                  { icon: CreditCard, title: "Full Checkouts", description: "Test the entire payment flow", color: "from-cyan-500 to-blue-500", demoHash: "payment" }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <Link to={`/demos#${item.demoHash}`} key={item.title}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.1 + index * 0.08 }}
                        whileHover={{ y: -6, scale: 1.05 }}
                        className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white hover:shadow-xl transition-all duration-200 group cursor-pointer border border-gray-200 hover:border-indigo-300"
                      >
                        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${item.color} mx-auto mb-3 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="text-sm font-medium group-hover:text-indigo-600 transition-colors">{item.title}</h4>
                        <p className="text-xs text-gray-600 mt-2">{item.description}</p>
                      </motion.div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* How It Works — 4 Steps */}
          <section id="how-it-works-steps" className="py-16 bg-gradient-to-b from-gray-50/50 to-white">
            <div className="container max-w-6xl mx-auto px-4">
              <motion.div
                className="text-center mb-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 mb-4">
                  <Rocket className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
                  From Idea to Insight in 4 Steps
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                  Pick your validation type, build your flow, launch, and read the real signal.
                </p>
              </motion.div>
                
                <div className="relative">
                  {/* Connection line - Desktop */}
                  <div className="absolute left-1/2 top-12 bottom-12 w-0.5 bg-gradient-to-b from-indigo-300 to-violet-300 hidden md:block"></div>
                  
                  {/* Mobile Connection Line */}
                  <div className="absolute left-6 top-16 bottom-16 w-0.5 bg-gradient-to-b from-indigo-200 to-violet-200 md:hidden"></div>
                  
                  {/* Steps */}
                  <div className="space-y-8 md:space-y-24 relative">
                    {/* Step 1 */}
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="md:flex items-center"
                    >
                      {/* Mobile Card Layout */}
                      <div className="md:hidden bg-white/80 rounded-xl p-6 shadow-sm border border-indigo-100/50 ml-12 relative">
                        <div className="absolute -left-8 top-6">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold shadow-lg z-10">1</div>
                        </div>
                        <div className="flex items-center mb-3">
                          <h3 className="text-lg font-semibold">
                            Choose What You're Testing
                          </h3>
                          <div className="ml-2 w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                            <Palette className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Pick your validation type — payment intent, waitlist signups, or pricing sensitivity. Your selection pre-loads the right template.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Choose from 8 proven test templates</span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Add your product details</span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Set your pricing (or test multiple)</span>
                          </li>
                        </ul>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden md:block md:w-1/2 pr-4 md:pr-8 md:text-right mb-6 md:mb-0">
                        <div className="flex items-center md:justify-end mb-2 gap-2">
                          <h3 className="text-lg font-medium">
                            Choose What You're Testing
                          </h3>
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                            <Palette className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                          Pick your validation type — payment intent, waitlist signups, or pricing sensitivity. Your selection pre-loads the right template.
                        </p>
                        <ul className="space-y-1">
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Choose from 8 proven test templates</span>
                            <Check className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                          </li>
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Add your product details</span>
                            <Check className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                          </li>
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Set your pricing (or test multiple)</span>
                            <Check className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                          </li>
                        </ul>
                      </div>
                      
                      <div className="md:w-12 hidden md:flex justify-center">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold shadow-lg z-10">1</div>
                      </div>
                      
                      <div className="md:w-1/2 pl-8 hidden md:block">
                        <div className="bg-white/80 rounded-lg p-4 shadow-sm">
                          <div 
                            className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg border border-white/10 group cursor-pointer"
                            onClick={() => handleFullscreen(video1Ref)}
                            onMouseEnter={() => setIsHoveringVideo1(true)}
                            onMouseLeave={() => setIsHoveringVideo1(false)}
                          >
                            <LazyVideo
                              ref={video1Ref}
                              src="/videos/create-flow.mp4"
                              className="w-full h-full rounded"
                            />
                            
                            {/* Fullscreen overlay */}
                            <AnimatePresence>
                              {isHoveringVideo1 && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="absolute inset-0 bg-black/20 flex items-center justify-center"
                                >
                                  <motion.div
                                    initial={{ scale: 0.8 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0.8 }}
                                    className="bg-white/90 backdrop-blur-sm p-2 rounded-lg"
                                  >
                                    <Maximize2 className="w-6 h-6 text-gray-800" />
                                  </motion.div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Step 2 */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="md:flex items-center"
                    >
                      {/* Mobile Card Layout */}
                      <div className="md:hidden bg-white/80 rounded-xl p-6 shadow-sm border border-violet-100/50 ml-12 relative">
                        <div className="absolute -left-8 top-6">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold shadow-lg z-10">2</div>
                        </div>
                        <div className="flex items-center mb-3">
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mr-2">
                            <Rocket className="w-4 h-4 text-white" />
                          </div>
                          <h3 className="text-lg font-semibold">
                            Build Your Flow
                          </h3>
                      </div>
                        <p className="text-gray-600 text-sm mb-4">
                          A guided builder walks you through every step: product details, flow type, design, and your thank-you page.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-violet-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Step-by-step guided builder</span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-violet-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">No design skills needed</span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-violet-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">No blank page paralysis</span>
                          </li>
                        </ul>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden md:block md:w-1/2 pl-4 md:pl-8 mb-6 md:mb-0">
                        <div className="bg-white/80 rounded-lg p-4 shadow-sm">
                          <div 
                            className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg border border-white/10 group cursor-pointer"
                            onClick={() => handleFullscreen(video2Ref)}
                            onMouseEnter={() => setIsHoveringVideo2(true)}
                            onMouseLeave={() => setIsHoveringVideo2(false)}
                          >
                            <LazyVideo
                              ref={video2Ref}
                              src="/videos/share-flow.mp4"
                              className="w-full h-full rounded"
                            />
                            
                            {/* Fullscreen overlay */}
                            <AnimatePresence>
                              {isHoveringVideo2 && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="absolute inset-0 bg-black/20 flex items-center justify-center"
                                >
                                  <motion.div
                                    initial={{ scale: 0.8 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0.8 }}
                                    className="bg-white/90 backdrop-blur-sm p-2 rounded-lg"
                                  >
                                    <Maximize2 className="w-6 h-6 text-gray-800" />
                                  </motion.div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                      
                      <div className="md:w-12 hidden md:flex justify-center">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold shadow-lg z-10">2</div>
                      </div>
                      
                      <div className="hidden md:block md:w-1/2 pr-4 md:pr-8 md:text-right mb-6 md:mb-0">
                        <div className="flex items-center md:justify-end mb-2 gap-2">
                          <h3 className="text-lg font-medium">
                            Build Your Flow
                          </h3>
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                            <Rocket className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                          A guided builder walks you through every step: product details, flow type, design, and your thank-you page.
                        </p>
                        <ul className="space-y-1">
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Step-by-step guided builder</span>
                            <Check className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" />
                          </li>
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">No design skills needed</span>
                            <Check className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" />
                          </li>
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">No blank page paralysis</span>
                            <Check className="h-3.5 w-3.5 text-violet-500 mt-0.5 shrink-0" />
                          </li>
                        </ul>
                      </div>
                    </motion.div>

                    {/* Step 3 */}
                    <motion.div 
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="md:flex items-center"
                    >
                      {/* Mobile Card Layout */}
                      <div className="md:hidden bg-white/80 rounded-xl p-6 shadow-sm border border-blue-100/50 ml-12 relative">
                        <div className="absolute -left-8 top-6">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white flex items-center justify-center font-bold shadow-lg z-10">3</div>
                        </div>
                        <div className="flex items-center mb-3">
                          <h3 className="text-lg font-semibold">
                            Launch and Share
                          </h3>
                          <div className="ml-2 w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Start your free trial and publish in one click. Share a direct link or embed it anywhere your audience already is.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Publish in one click</span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Share a direct link or embed</span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">Reach your audience where they are</span>
                          </li>
                        </ul>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden md:block md:w-1/2 pr-4 md:pr-8 md:text-right mb-6 md:mb-0">
                        <div className="flex items-center md:justify-end mb-2 gap-2">
                          <h3 className="text-lg font-medium">
                            Launch and Share
                          </h3>
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                          Start your free trial and publish in one click. Share a direct link or embed it anywhere your audience already is.
                        </p>
                        <ul className="space-y-1">
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Publish in one click</span>
                            <Check className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                          </li>
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Share a direct link or embed</span>
                            <Check className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                          </li>
                          <li className="flex items-start gap-2 text-sm md:justify-end">
                            <span className="text-gray-600">Reach your audience where they are</span>
                            <Check className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                          </li>
                        </ul>
                      </div>
                      
                      <div className="md:w-12 hidden md:flex justify-center">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white flex items-center justify-center font-bold shadow-lg z-10">3</div>
                      </div>
                      
                      <div className="md:w-1/2 pl-8 hidden md:block">
                        <div className="bg-white/80 rounded-lg p-4 shadow-sm">
                          <div 
                            className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg border border-white/10 group cursor-pointer"
                            onClick={() => handleFullscreen(video3Ref)}
                            onMouseEnter={() => setIsHoveringVideo3(true)}
                            onMouseLeave={() => setIsHoveringVideo3(false)}
                          >
                            <LazyVideo
                              ref={video3Ref}
                              src="/videos/insights.mp4"
                              className="w-full h-full rounded"
                            />
                            
                            {/* Fullscreen overlay */}
                            <AnimatePresence>
                              {isHoveringVideo3 && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="absolute inset-0 bg-black/20 flex items-center justify-center"
                                >
                                  <motion.div
                                    initial={{ scale: 0.8 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0.8 }}
                                    className="bg-white/90 backdrop-blur-sm p-2 rounded-lg"
                                  >
                                    <Maximize2 className="w-6 h-6 text-gray-800" />
                                  </motion.div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Step 4 - The Result */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="md:flex items-center"
                    >
                      {/* Mobile Card Layout */}
                      <div className="md:hidden bg-white/80 rounded-xl p-6 shadow-sm border border-green-100/50 ml-12 relative">
                        <div className="absolute -left-8 top-6">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center justify-center font-bold shadow-lg z-10">4</div>
                        </div>
                        <div className="flex items-center mb-3">
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mr-2">
                            <Target className="w-4 h-4 text-white" />
                          </div>
                          <h3 className="text-lg font-semibold">
                            Read the Real Signal
                          </h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                          Your dashboard shows who clicked, who tried to convert, and exactly where people dropped off.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-3 text-sm">
                            <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">People clicked "Buy" = <span className="font-medium text-green-700">Build it now</span></span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <div className="w-4 h-4 rounded bg-amber-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <span className="text-amber-600 text-xs font-bold">⚠</span>
                            </div>
                            <span className="text-gray-600">Just browsing = <span className="font-medium text-amber-700">Needs work</span></span>
                          </li>
                          <li className="flex items-start gap-3 text-sm">
                            <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">No clicks = <span className="font-medium text-red-700">Kill it, try something else</span></span>
                          </li>
                        </ul>
                      </div>

                      {/* Desktop Layout */}
                      <div className="md:w-1/2 pr-8 hidden md:block">
                        <div className="bg-white/80 rounded-lg p-4 shadow-sm">
                          <div className="bg-gradient-to-r from-green-50/80 to-emerald-50/80 rounded-lg p-6 border border-green-100/50">
                            <div className="text-center">
                              <div className="flex justify-center mb-3">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                                  <Target className="w-7 h-7 text-white" />
                                </div>
                              </div>
                              <h4 className="text-lg font-semibold text-gray-900 mb-2">Read the Real Signal</h4>
                              <p className="text-sm text-gray-600">Your dashboard shows who clicked, who tried to convert, and exactly where people dropped off.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="md:w-12 hidden md:flex justify-center">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center justify-center font-bold shadow-lg z-10">4</div>
                      </div>
                      
                      <div className="hidden md:block md:w-1/2 pl-4 md:pl-8 mb-6 md:mb-0">
                        <div className="flex items-center mb-2">
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mr-2">
                            <Target className="w-4 h-4 text-white" />
                          </div>
                          <h3 className="text-lg font-medium">
                            Read the Real Signal
                          </h3>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                          Your dashboard shows who clicked, who tried to convert, and exactly where people dropped off.
                        </p>
                        <ul className="space-y-1">
                          <li className="flex items-start gap-2 text-sm">
                            <Check className="h-3.5 w-3.5 text-green-500 mt-0.5" />
                            <span className="text-gray-600">People clicked "Buy" = <span className="font-medium text-green-700">Build it now</span></span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <div className="w-3.5 h-3.5 rounded bg-amber-100 flex items-center justify-center mt-0.5">
                              <span className="text-amber-600 text-[10px] font-bold">⚠</span>
                            </div>
                            <span className="text-gray-600">Just browsing = <span className="font-medium text-amber-700">Needs work</span></span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <X className="h-3.5 w-3.5 text-red-500 mt-0.5" />
                            <span className="text-gray-600">No clicks = <span className="font-medium text-red-700">Kill it, try something else</span></span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  </div>
                </div>

              {/* CTA */}
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Link to="/create">
                  <Button size="lg" className="gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-md hover:shadow-lg transition-all duration-300 h-12">
                    Start Testing Your Idea <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </section>

          {/* Flow Types Section with Tech Giants Integration */}

          <section id="features" className="py-16 bg-white">
            <div className="container max-w-6xl mx-auto px-4">
              <motion.div 
                className="text-center mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                    <Search className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
                  Multiple Flow Types
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                  Test payments, waitlists, subscriptions, pricing pages, pre-orders, referrals, newsletters, or discount offers. One platform for every validation scenario.
                </p>
              </motion.div>


              {/* Flow Types Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {flowTypes.map((flow, index) => (
                  <motion.div
                    key={flow.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group relative"
                  >
                    {flow.isPopular && (
                      <div className="absolute -top-2 -right-2 z-10">
                        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                          <Star className="w-3 h-3 fill-white" />
                          Popular
                        </div>
                      </div>
                    )}
                    <Link to={`/demos#${flow.id}`} className="block h-full">
                    <div className="h-full p-6 rounded-2xl bg-white border border-gray-200 hover:border-indigo-200 hover:shadow-lg transition-all duration-300 flex flex-col group">
                      <div className="flex items-start mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                          {(() => {
                            const Icon = flow.icon;
                            return <Icon className="w-6 h-6 text-white" />;
                          })()}
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{flow.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{flow.description}</p>
                      <div className="mt-auto">
                          <span 
                          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700 font-medium group/link"
                        >
                          See Demo 
                          <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Product Philosophy Section */}
          <section className="py-14 bg-gradient-to-b from-white to-gray-50/80 overflow-hidden">
            <div className="container max-w-5xl mx-auto px-4">
              <motion.div
                className="text-center mb-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 mb-4">
                  <Lightbulb className="w-3.5 h-3.5 text-violet-600" />
                  <span className="text-xs font-medium text-violet-600">Product Philosophy</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Built for{' '}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">Founders</span>,{' '}
                  Not Marketers
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Card 1 — Cold Start */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="rounded-2xl border border-gray-200/80 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-sm shadow-indigo-500/20">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900">The Cold-Start Problem — Solved</h3>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed mb-3">
                    Most tools hand you a blank canvas. Founders don't fail because their ideas are bad — <span className="text-gray-700 font-medium">they fail because they can't figure out what to do next.</span>
                  </p>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">
                    You choose your goal <span className="text-gray-700 font-medium">before</span> you sign up. That selection pre-loads the right template, removing every distraction.
                  </p>
                  <div className="flex items-start gap-2.5 p-3 rounded-xl bg-indigo-50/60 border border-indigo-100/80">
                    <Zap className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      You arrive at a flow <span className="font-semibold text-indigo-700">already 40% built</span> for your use case. The only question left: <span className="italic">what are you testing?</span>
                    </p>
                  </div>
                </motion.div>

                {/* Card 2 — Paywall */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="rounded-2xl border border-gray-200/80 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm shadow-violet-500/20">
                      <Eye className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900">The Paywall Comes Last</h3>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed mb-3">
                    The trial starts <span className="text-gray-700 font-medium">at publish</span> — after you've built something real. You're not paying for potential. You're launching something you already made.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { text: "Flow visible behind modal", icon: Eye },
                      { text: "Best plan pre-selected", icon: DollarSign },
                      { text: "Trust signals in checkout", icon: Check },
                      { text: "High-intent, not wasted", icon: Zap },
                    ].map((item, i) => {
                      const ItemIcon = item.icon;
                      return (
                        <motion.div
                          key={item.text}
                          initial={{ opacity: 0, scale: 0.95 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.25, delay: 0.3 + i * 0.06 }}
                          className="flex items-center gap-2 p-2 rounded-lg bg-violet-50/50 border border-violet-100/50"
                        >
                          <ItemIcon className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                          <span className="text-xs text-gray-600">{item.text}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50/50 to-white">
            <div className="container max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                    <Rocket className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
                  Choose Your Testing Plan
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  From solo founders validating their first idea to teams testing at scale.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {pricingTiers.map((tier) => (
                  <div key={tier.name} className={`relative rounded-2xl p-8 bg-white shadow-lg border ${tier.isPopular ? 'border-violet-500 shadow-violet-200' : 'border-gray-200'} transition-all duration-200 hover:shadow-xl`}>
                    {tier.isPopular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                          BEST VALUE
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold mb-3">{tier.name}</h3>
                      <p className="text-gray-600 mb-6">{tier.description}</p>
                      
                      <div className="mb-4">
                        <div className="flex items-baseline justify-center">
                          <span className="text-5xl font-bold text-indigo-600">${tier.price}</span>
                          <span className="text-gray-600">{tier.period}</span>
                        </div>

                        {/* Billing note + savings */}
                        <div className="h-6 mt-2">
                          {(tier as any).billingNote ? (
                            <p className="text-sm text-gray-500">{(tier as any).billingNote}</p>
                          ) : null}
                        </div>

                        {/* Prominent Free Trial Badge */}
                        <div className="mt-4 mb-2">
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-full">
                            <span className="text-lg">🎁</span>
                            <span className="text-sm font-semibold text-green-700">
                              Start FREE for 14 days
                            </span>
                          </div>
                        </div>

                        <p className="text-xs text-gray-600 mb-1">
                          {tier.isPopular ? "Same great features, better price" : "No charges until you're convinced"}
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature, idx) => {
                        const Icon = feature.icon;
                        return (
                          <li key={idx} className="flex items-center gap-3 text-sm">
                            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-gray-700">{feature.text}</span>
                          </li>
                        );
                      })}
                    </ul>

                    <Button
                      className="w-full rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-md hover:shadow-xl transition-all duration-200 h-12 font-semibold"
                      onClick={async () => {
                        if (!user) {
                          navigate('/auth?returnTo=/subscription');
                        } else {
                          setLoadingPlanId(tier.planId);
                          await startSubscription(tier.planId);
                          setLoadingPlanId(null);
                        }
                      }}
                      disabled={isLoading}
                    >
                      {loadingPlanId === tier.planId ? "Processing..." : "Start Free Trial"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <Suspense fallback={<SectionLoader />}>
            <TestimonialsSection />
          </Suspense>

          {/* CTA Banner */}
          <section className="py-20 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent)] pointer-events-none" />
            <div className="container max-w-4xl mx-auto px-4 text-center relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Your future customers are deciding right now. Are you watching?
                </h2>
                <div className="flex justify-center mt-8">
                  <Link to="/create">
                    <Button size="lg" className="gap-2 rounded-full bg-white text-indigo-700 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-200 h-14 font-semibold px-8 text-base">
                      Start Testing Free — 14 Days <ArrowRight className="w-5 h-5" />
                    </Button>
                  </Link>
                </div>
                <p className="text-white/70 text-sm mt-6">
                  Start your 14-day free trial today. No payment is required now, and you can cancel at any time.
                </p>
              </motion.div>
            </div>
          </section>

          {/* FAQ Section */}
          <Suspense fallback={<SectionLoader />}>
            <FAQSection />
          </Suspense>
        </div>
      </div>
    </>
  );
};

export default Index;
